import React, { useState, useEffect } from 'react';
import DatePicker from "react-multi-date-picker";
import "react-multi-date-picker/styles/colors/purple.css";
import Select from 'react-select';
import PageHeader from '../../components/PageHeader';
import Swal from 'sweetalert2'; // ✅ Import SweetAlert2
const LMS = process.env.REACT_APP_API_LMS;
const token = process.env.REACT_APP_API_TOKEN;

const IFGenerateScheduleForm = () => {
  const [formData, setFormData] = useState({
    course: '',            // will store course_id
    batchType: 'Regular',
    startDate: '',
    endDate: '',
    holidayDates: [],      // ALWAYS an array of 'YYYY-MM-DD'
    startTime: '09:00',
    endTime: '10:00',
    frequency: 'Weekly',
    daysOfWeek: {
      Monday: true,
      Tuesday: true,
      Wednesday: true,
      Thursday: true,
      Friday: true,
      Saturday: false,
      Sunday: false
    }
  });

  const [errors, setErrors] = useState({});
  const [showPreview, setShowPreview] = useState(false);
  const [scheduleData, setScheduleData] = useState([]);
  const [isGenerating, setIsGenerating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [submitMessage, setSubmitMessage] = useState({ type: '', text: '' });

  const [batchId, setBatchId] = useState(null);
  const [programId, setProgramId] = useState(null);
  const [meetLink, setMeetLink] = useState('');
  const [meetLinkLoading, setMeetLinkLoading] = useState(false);

  // Courses from API
  const [courses, setCourses] = useState([]);
  const [isLoadingCourses, setIsLoadingCourses] = useState(false);
  const [courseLoadError, setCourseLoadError] = useState('');

  const batchTypes = [
    { value: 'Regular', label: 'Regular'},
    { value: 'Weekend', label: 'Weekend'}
  ];

  const frequencies = [
    { value: 'Daily', label: 'Daily'},
    { value: 'Weekly', label: 'Weekly'},
    { value: 'Bi-weekly', label: 'Bi-weekly'},
    { value: 'Monthly', label: 'Monthly'}
  ];

  // 🔗 Read batch & program from URL: /teacher-generate-schedule?batch=4&program=71
  useEffect(() => {
    try {
      const params = new URLSearchParams(window.location.search);
      const batch = params.get('batch');
      const program = params.get('program');

      if (batch) setBatchId(batch);
      if (program) setProgramId(program);
    } catch (e) {
      console.error("Error reading query params:", e);
    }
  }, []);

  // 🎓 Load courses from API based on programId
  useEffect(() => {
    if (!programId || !LMS || !token) return;

    const fetchCourses = async () => {
      setIsLoadingCourses(true);
      setCourseLoadError('');

      try {
        const response = await fetch(`${LMS}/api/courses-by-program`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Accept': 'application/json',
            'Authorization': token
          },
          body: JSON.stringify({
            program_ids: [Number(programId)]
          })
        });

        if (!response.ok) {
          const errorData = await response.json().catch(() => ({}));
          throw new Error(errorData?.message || `Failed to load courses (status ${response.status})`);
        }

        const data = await response.json();
        const apiCourses = data?.data || [];
        setCourses(apiCourses);

      } catch (error) {
        console.error('Error loading courses:', error);
        setCourseLoadError(error.message || 'Failed to load courses. Please refresh the page or try again later.');
        // SweetAlert for error
        Swal.fire({
          icon: 'error',
          title: 'Error',
          text: error.message || 'Failed to load courses. Please refresh the page or try again later.',
          confirmButtonText: 'OK'
        });
      } finally {
        setIsLoadingCourses(false);
      }
    };

    fetchCourses();
  }, [programId]);

  // Auto-populate days based on batch type
  useEffect(() => {
    let updatedDaysOfWeek = {};
    switch (formData.batchType) {
      case 'Regular':
        updatedDaysOfWeek = {
          Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true,
          Saturday: false, Sunday: false
        };
        break;
      case 'Weekend':
        updatedDaysOfWeek = {
          Monday: false, Tuesday: false, Wednesday: false, Thursday: false, Friday: false,
          Saturday: true, Sunday: true
        };
        break;
      case 'Intensive':
        updatedDaysOfWeek = {
          Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true,
          Saturday: true, Sunday: true
        };
        break;
      default:
        return;
    }

    setFormData(prev => ({
      ...prev,
      daysOfWeek: updatedDaysOfWeek
    }));
  }, [formData.batchType]);

  const getEffectiveEndDate = () => {
    if (formData.endDate) return formData.endDate;
    if (!formData.startDate) return '';
    const start = new Date(formData.startDate);
    const end = new Date(start.getTime() + 90 * 24 * 60 * 60 * 1000); // +90 days
    return end.toISOString().split('T')[0];
  };

  const getSelectedDaysLowercase = () => {
    return Object.keys(formData.daysOfWeek)
      .filter(day => formData.daysOfWeek[day])
      .map(day => day.toLowerCase());
  };

  const getSelectedCourseLabel = () => {
    if (!formData.course) return 'Not selected';
    const course = courses.find(c => String(c.id) === String(formData.course));
    return course ? course.title : formData.course;
  };

  const calculateDuration = () => {
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01T${formData.startTime}`);
      const endTime = new Date(`2000-01-01T${formData.endTime}`);
      const durationMs = endTime - startTime;
      const durationHours = Math.round((durationMs / (1000 * 60 * 60)) * 10) / 10;
      return durationHours > 0 ? `${durationHours} hours` : '';
    }
    return '';
  };

  const fetchMeetLink = async () => {
    if (!batchId || !LMS || !token) return;
    try {
      setMeetLinkLoading(true);
      const res = await fetch(`${LMS}/api/zoom-link?batch_id=${Number(batchId)}`, {
        method: 'GET',
        headers: {
          'Authorization': token,
          'Accept': 'application/json',
          'Content-Type': 'application/json'
        }
      });
      const data = await res.json().catch(() => ({}));
      const link = data?.link || data?.data?.link || '';
      if (link) {
        setMeetLink(link);
      } else {
        Swal.fire({
          icon: 'warning',
          title: 'No Link Found',
          text: data?.message || 'No meet link was returned by the server.',
          confirmButtonText: 'OK'
        });
      }
    } catch (e) {
      console.error('Failed to fetch meet link', e);
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: e?.message || 'Failed to fetch meet link. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setMeetLinkLoading(false);
    }
  };

  const getSelectedDaysCount = () => {
    return Object.values(formData.daysOfWeek).filter(Boolean).length;
  };

  // ✅ Validation
  const validateForm = () => {
    const newErrors = {};

    if (!formData.course) newErrors.course = 'Course is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    if (!formData.startTime) newErrors.startTime = 'Start time is required';
    if (!formData.endTime) newErrors.endTime = 'End time is required';

    // Date validation
    if (formData.startDate) {
      const startDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);

      if (startDate < today) {
        newErrors.startDate = 'Start date cannot be in the past';
      }
    }

    if (formData.startDate && formData.endDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);

      if (endDate <= startDate) {
        newErrors.endDate = 'End date must be after start date';
      }

      // Check if date range is reasonable (not more than 2 years)
      const timeDiff = endDate - startDate;
      const daysDiff = timeDiff / (1000 * 60 * 60 * 24);
      if (daysDiff > 730) {
        newErrors.endDate = 'Date range cannot exceed 2 years';
      }
    }

    // Time validation
    if (formData.startTime && formData.endTime) {
      const startTime = new Date(`2000-01-01T${formData.startTime}`);
      const endTime = new Date(`2000-01-01T${formData.endTime}`);

      if (endTime <= startTime) {
        newErrors.endTime = 'End time must be after start time';
      }

      const durationMs = endTime - startTime;
      const durationHours = durationMs / (1000 * 60 * 60);

      if (durationHours < 0.5) {
        newErrors.endTime = 'Session must be at least 30 minutes long';
      } else if (durationHours > 8) {
        newErrors.endTime = 'Session cannot exceed 8 hours';
      }
    }

    // Days validation
    const selectedDays = getSelectedDaysLowercase();
    if (selectedDays.length === 0) {
      newErrors.daysOfWeek = 'At least one day must be selected';
    }

    // Holiday dates validation
    if (formData.holidayDates && formData.holidayDates.length > 0) {
      const invalidDates = formData.holidayDates.filter(date => {
        const dateObj = new Date(date);
        return isNaN(dateObj.getTime());
      });

      if (invalidDates.length > 0) {
        newErrors.holidayDates = 'Some holiday dates are invalid';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Generic handlers
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));

    if (errors[name]) {
      setErrors(prev => ({
        ...prev,
        [name]: ''
      }));
    }
  };

  const handleBatchTypeChange = (e) => {
    const { value } = e.target;
    setFormData(prev => ({
      ...prev,
      batchType: value
    }));

    if (errors.batchType) {
      setErrors(prev => ({ ...prev, batchType: '' }));
    }
  };

  const handleDayChange = (day) => {
    setFormData(prev => ({
      ...prev,
      daysOfWeek: {
        ...prev.daysOfWeek,
        [day]: !prev.daysOfWeek[day]
      }
    }));

    if (errors.daysOfWeek) {
      setErrors(prev => ({ ...prev, daysOfWeek: '' }));
    }
  };

  const removeHolidayDate = (index) => {
    setFormData(prev => ({
      ...prev,
      holidayDates: prev.holidayDates.filter((_, i) => i !== index),
    }));
  };

  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return dateString;
    return date.toLocaleDateString('en-IN', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  // 👁 Preview Schedule (calls API /batch-schedule/preview)
  const handlePreview = async () => {
    setSubmitMessage({ type: '', text: '' });

    if (!validateForm()) {
      // SweetAlert for validation errors
      Swal.fire({
        icon: 'warning',
        title: 'Fill the Form Completely',
        text: 'Please complete the required fields before generating the preview.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Require meet link before preview
    if (!meetLink) {
      Swal.fire({
        icon: 'warning',
        title: 'Meeting Link Required',
        text: 'Please click "Get Link" to fetch the meeting link before generating the preview.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!LMS || !token) {
      // SweetAlert for missing API config
      Swal.fire({
        icon: 'error',
        title: 'Configuration Error',
        text: 'API configuration missing. Please check REACT_APP_API_LMS and REACT_APP_API_TOKEN.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const frequencyApi = formData.frequency.toLowerCase();  // weekly, daily, etc.
    const daysApi = getSelectedDaysLowercase();             // ["monday","wednesday"]
    const endDateApi = getEffectiveEndDate();
    const holidaysApi = (formData.holidayDates || []).join(',');  // "2025-02-10,2025-02-15"

    setIsGenerating(true);
    setShowPreview(false);
    setScheduleData([]);

    try {
      const response = await fetch(`${LMS}/api/batch-schedule/preview`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          frequency: frequencyApi,
          start_date: formData.startDate,
          end_date: endDateApi,
          days: daysApi,
          holidays: holidaysApi
        })
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData?.message || `Preview request failed (status ${response.status})`);
      }

      const data = await response.json();

      if (!data || !Array.isArray(data.schedule) || data.schedule.length === 0) {
        // SweetAlert for no schedule entries
        Swal.fire({
          icon: 'warning',
          title: 'No Schedule',
          text: 'No schedule entries returned from server for the selected parameters.',
          confirmButtonText: 'OK'
        });
        setShowPreview(false);
        setScheduleData([]);
        return;
      }

      // Map API preview response to UI scheduleData
      const schedule = data.schedule.map((item, index) => ({
        id: index + 1,
        date: item.date,
        day: item.day,
        startTime: formData.startTime,
        endTime: formData.endTime,
        time: `${formData.startTime} - ${formData.endTime}`,
        holiday: item.is_holiday ? 'Yes' : 'No',
        status: item.is_holiday ? 'Holiday' : 'Scheduled'
      }));

      setScheduleData(schedule);
      setShowPreview(true);

      // SweetAlert for success
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: `Successfully generated ${schedule.length} schedule entries for preview.`,
        confirmButtonText: 'OK'
      });

      setTimeout(() => {
        const previewElement = document.querySelector('#schedule-preview');
        if (previewElement) {
          previewElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }, 100);

    } catch (error) {
      console.error('Error generating schedule preview:', error);
      // SweetAlert for error
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to generate schedule preview from server. Please try again.',
        confirmButtonText: 'OK'
      });
      setShowPreview(false);
      setScheduleData([]);
    } finally {
      setIsGenerating(false);
    }
  };

  // 💾 Save / Generate Schedule (calls API /batch-schedule/generate)
  const handleSaveSchedule = async () => {
    if (!validateForm()) {
      // SweetAlert for validation errors
      Swal.fire({
        icon: 'warning',
        title: 'Validation Error',
        text: 'Please fix the errors below before saving.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (scheduleData.length === 0) {
      // SweetAlert for no preview generated
      Swal.fire({
        icon: 'warning',
        title: 'No Preview',
        text: 'Please generate a preview first before saving.',
        confirmButtonText: 'OK'
      });
      return;
    }

    // Require meet link before saving
    if (!meetLink) {
      Swal.fire({
        icon: 'warning',
        title: 'Meeting Link Required',
        text: 'Please click "Get Link" to fetch the meeting link before saving the schedule.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!LMS || !token) {
      // SweetAlert for missing API config
      Swal.fire({
        icon: 'error',
        title: 'Configuration Error',
        text: 'API configuration missing. Please check REACT_APP_API_LMS and REACT_APP_API_TOKEN.',
        confirmButtonText: 'OK'
      });
      return;
    }

    if (!batchId) {
      // SweetAlert for missing batch ID
      Swal.fire({
        icon: 'error',
        title: 'Missing Batch ID',
        text: 'Batch ID is missing from URL. Please reopen the page from the correct link.',
        confirmButtonText: 'OK'
      });
      return;
    }

    const batchTypeMap = {
      Regular: 'regular',
      Weekend: 'weekend',
      Intensive: 'intensive'
    };

    const frequencyApi = formData.frequency.toLowerCase();
    const daysApi = getSelectedDaysLowercase();
    const endDateApi = getEffectiveEndDate();
    const holidaysApi = (formData.holidayDates || []).join(',');

    setIsSaving(true);
    setSubmitMessage({ type: '', text: '' });

    try {
      const response = await fetch(`${LMS}/api/batch-schedule/generate`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json',
          'Authorization': token
        },
        body: JSON.stringify({
          batch_id: Number(batchId),
          course_id: Number(formData.course),
          frequency: frequencyApi,
          start_date: formData.startDate,
          end_date: endDateApi,
          batch_type: batchTypeMap[formData.batchType] || 'regular',
          days: daysApi,
          start_time: formData.startTime,
          end_time: formData.endTime,
          holidays: holidaysApi,
          meet_url: meetLink || null
        })
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok || data.status === false) {
        const msg = data?.message || `Failed to generate schedule (status ${response.status}).`;
        throw new Error(msg);
      }

      // ✅ SweetAlert on success
      Swal.fire({
        icon: 'success',
        title: 'Success!',
        text: data?.message || `Schedule generated and saved successfully for batch.`,
        confirmButtonText: 'OK'
      }).then(() => {
        // ✅ Redirect to batch list page
        window.location.href = '/teacher-batches';
      });

    } catch (error) {
      console.error('Error saving schedule:', error);
      // SweetAlert for error
      Swal.fire({
        icon: 'error',
        title: 'Error',
        text: error.message || 'Failed to save schedule. Please try again.',
        confirmButtonText: 'OK'
      });
    } finally {
      setIsSaving(false);
    }
  };

  const resetForm = () => {
    setFormData({
      course: '',
      batchType: 'Regular',
      startDate: '',
      endDate: '',
      holidayDates: [],
      startTime: '09:00',
      endTime: '10:00',
      frequency: 'Weekly',
      daysOfWeek: {
        Monday: true, Tuesday: true, Wednesday: true, Thursday: true, Friday: true,
        Saturday: false, Sunday: false
      }
    });
    setErrors({});
    setSubmitMessage({ type: '', text: '' });
    setShowPreview(false);
    setScheduleData([]);
    setMeetLink('');
  };

  // react-select helpers
  const courseOptions = courses.map(c => ({ value: c.id, label: c.title }));
  const selectedCourse = courseOptions.find(opt => opt.value == formData.course) || null;

  const batchTypeOptions = batchTypes.map(bt => ({
    value: bt.value,
    label: `${bt.label}`
  }));
  const selectedBatchType = batchTypeOptions.find(opt => opt.value === formData.batchType) || null;

  const frequencyOptions = frequencies.map(f => ({
    value: f.value,
    label: `${f.label}`
  }));
  const selectedFrequency = frequencyOptions.find(opt => opt.value === formData.frequency) || null;

  return (
    <div>
      <PageHeader
        title={`Generate Schedule `}
        breadcrumbs={[
          { label: 'Dashboard', path: '/teacher-dashboard' },
          { label: 'Batches', path: '/teacher-batches' },
          { label: `Generate Schedule`, active: true },
        ]}
      />

      {/* Alert Messages */}
      {/* {submitMessage.text && (
        <div className="row mb-3">
          <div className="col-12">
            <div className={`alert alert-${submitMessage.type} alert-dismissible text-white fade show`} role="alert">
              <strong>
                {submitMessage.type === 'success' ? '✓ Success!' :
                  submitMessage.type === 'warning' ? '⚠ Warning!' : '⚠ Error!'}
              </strong>
              {' ' + submitMessage.text}
              <button
                type="button"
                className="btn-close text-white"
                onClick={() => setSubmitMessage({ type: '', text: '' })}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )} */}

      {/* Courses load error */}
      {/* {courseLoadError && (
        <div className="row mb-3">
          <div className="col-12">
            <div className="alert alert-warning alert-dismissible fade show" role="alert">
              {courseLoadError}
              <button
                type="button"
                className="btn-close"
                onClick={() => setCourseLoadError('')}
                aria-label="Close"
              ></button>
            </div>
          </div>
        </div>
      )} */}

      {/* Form */}
      <div className="row">
        <div className="col-12">
          <div className="card skyblueborder">
            <div className="card-header skyblue-header d-flex justify-content-between align-items-center flex-wrap gap-2">
              <h5 className="mb-0 text-primary">
                Schedule Generation
              </h5>
              <button
                type="button"
                className="btn btn-sm btn-link text-decoration-none text-primary fw-semibold p-0"
                onClick={() => window.location.href = '/teacher-batches'}
              >
                <i className="fas fa-arrow-left me-1"></i> Back to Batch List
              </button>
            </div>
            <div className="card-body p-2">
              {/* Course and Date Range */}
              <div className="row">
                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Course <span className="text-danger">*</span>
                  </label>
                  <Select
                    className={`${errors.course ? 'is-invalid' : ''}`}
                    classNamePrefix="select"
                    options={courseOptions}
                    value={selectedCourse}
                    onChange={(opt) => {
                      setFormData(prev => ({ ...prev, course: opt ? opt.value : '' }));
                      if (errors.course) setErrors(prev => ({ ...prev, course: '' }));
                    }}
                    isLoading={isLoadingCourses}
                    isDisabled={isLoadingCourses}
                    placeholder={isLoadingCourses ? 'Loading courses...' : 'Choose a course...'}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: errors.course ? '#dc3545' : base.borderColor,
                        backgroundColor: errors.course ? '#fef2f2' : base.backgroundColor,
                      }),
                    }}
                  />
                  {errors.course && <div className="invalid-feedback d-block">{errors.course}</div>}
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold text-dark">Batch Type</label>
                  <Select
                    classNamePrefix="select"
                    options={batchTypeOptions}
                    value={selectedBatchType}
                    onChange={(opt) => {
                      setFormData(prev => ({ ...prev, batchType: opt ? opt.value : 'Regular' }));
                      if (errors.batchType) setErrors(prev => ({ ...prev, batchType: '' }));
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: errors.batchType ? '#dc3545' : base.borderColor,
                        backgroundColor: errors.batchType ? '#fef2f2' : base.backgroundColor,
                      }),
                    }}
                  />
                  {errors.batchType && <div className="invalid-feedback d-block">{errors.batchType}</div>}
                </div>

                <div className="col-md-4 mb-3">
                  <label className="form-label fw-semibold text-dark">Frequency</label>
                  <Select
                    classNamePrefix="select"
                    options={frequencyOptions}
                    value={selectedFrequency}
                    onChange={(opt) => {
                      setFormData(prev => ({ ...prev, frequency: opt ? opt.value : 'Weekly' }));
                      if (errors.frequency) setErrors(prev => ({ ...prev, frequency: '' }));
                    }}
                    styles={{
                      control: (base) => ({
                        ...base,
                        borderColor: errors.frequency ? '#dc3545' : base.borderColor,
                        backgroundColor: errors.frequency ? '#fef2f2' : base.backgroundColor,
                      }),
                    }}
                  />
                  {errors.frequency && <div className="invalid-feedback d-block">{errors.frequency}</div>}
                </div>
              </div>

              {/* Time Fields */}
              <div className="row">
                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Start Date <span className="text-danger">*</span>
                  </label>
                  <input
                    type="date"
                    className={`form-control ${errors.startDate ? 'is-invalid' : ''}`}
                    name="startDate"
                    value={formData.startDate}
                    onChange={handleInputChange}
                    min={new Date().toISOString().split('T')[0]}
                    onFocus={(e) => e.target.showPicker?.()}
                    required
                  />
                  {errors.startDate && <div className="invalid-feedback d-block">{errors.startDate}</div>}
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold text-dark">End Date (Optional)</label>
                  <input
                    type="date"
                    className={`form-control ${errors.endDate ? 'is-invalid' : ''}`}
                    name="endDate"
                    value={formData.endDate}
                    onChange={handleInputChange}
                    min={formData.startDate || new Date().toISOString().split('T')[0]}
                    onFocus={(e) => e.target.showPicker?.()}
                  />
                  {errors.endDate && <div className="invalid-feedback d-block">{errors.endDate}</div>}
                  <div className="form-text">Leave empty for 90 days from start date</div>
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Start Time <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className={`form-control ${errors.startTime ? 'is-invalid' : ''}`}
                    name="startTime"
                    value={formData.startTime}
                    onChange={handleInputChange}
                    onFocus={(e) => e.target.showPicker?.()}
                    required
                  />
                  {errors.startTime && <div className="invalid-feedback d-block">{errors.startTime}</div>}
                </div>

                <div className="col-md-3 mb-3">
                  <label className="form-label fw-semibold text-dark">
                    End Time <span className="text-danger">*</span>
                  </label>
                  <input
                    type="time"
                    className={`form-control ${errors.endTime ? 'is-invalid' : ''}`}
                    name="endTime"
                    value={formData.endTime}
                    onChange={handleInputChange}
                    onFocus={(e) => e.target.showPicker?.()}
                    required
                  />
                  {errors.endTime && <div className="invalid-feedback d-block">{errors.endTime}</div>}
                </div>
              </div>

              {/* Days of Week */}
              <div className="row">
                <div className="col-12 mb-3">
                  <label className="form-label fw-semibold text-dark">
                    Days of Week <span className="text-danger">*</span>
                  </label>
                  <div className={`border rounded p-3 bg-light ${errors.daysOfWeek ? 'border-danger' : ''}`}>
                    <div className="row">
                      {Object.keys(formData.daysOfWeek).map(day => (
                        <div key={day} className="col-md-3 col-sm-6 mb-2">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id={day}
                              checked={formData.daysOfWeek[day]}
                              onChange={() => handleDayChange(day)}
                            />
                            <label className="form-check-label" htmlFor={day}>
                              {day}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                    <div className="mt-2">
                      <small className="text-muted">
                        {getSelectedDaysCount()} day(s) selected
                      </small>
                    </div>
                  </div>
                  {errors.daysOfWeek && <div className="text-danger small mt-1">{errors.daysOfWeek}</div>}
                </div>
              </div>

              {/* Meeting Link */}
              <div className="row">
                <div className="col-md-6 mb-3">
                  <label className="form-label fw-semibold text-dark">Meeting Link</label>
                  <div className="input-group">
                    <input
                      type="text"
                      className="form-control"
                      value={meetLink}
                      placeholder="Click Get Link to fetch"
                      disabled
                    />
                    <button
                      className="btn btn-outline-primary"
                      type="button"
                      onClick={fetchMeetLink}
                      disabled={meetLinkLoading || !batchId}
                    >
                      {meetLinkLoading ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Getting...
                        </>
                      ) : (
                        'Get Link'
                      )}
                    </button>
                  </div>
                </div>
              </div>

              {/* Holiday Dates */}
              <div className="row">
                <div className="col-12 mb-4">
                  <label className="form-label fw-semibold text-dark">Holiday Dates (Optional)</label>
                  <div className="input-group">
                    <DatePicker
                      multiple
                      value={formData.holidayDates}
                      onChange={(dates) => {
                        if (!dates) {
                          setFormData(prev => ({ ...prev, holidayDates: [] }));
                          if (errors.holidayDates) {
                            setErrors(prev => ({ ...prev, holidayDates: '' }));
                          }
                          return;
                        }

                        const normalized = Array.isArray(dates) ? dates : [dates];

                        const formatted = normalized
                          .map(d => {
                            if (!d) return null;
                            if (typeof d === 'string') return d;
                            if (typeof d === 'object' && typeof d.format === 'function') {
                              return d.format('YYYY-MM-DD');
                            }
                            try {
                              return new Date(d).toISOString().split('T')[0];
                            } catch {
                              return null;
                            }
                          })
                          .filter(Boolean);

                        setFormData(prev => ({ ...prev, holidayDates: formatted }));
                        if (errors.holidayDates) {
                          setErrors(prev => ({ ...prev, holidayDates: '' }));
                        }
                      }}
                      format="YYYY-MM-DD"
                      style={{ zIndex: 1055 }}
                      minDate={formData.startDate || null}
                      maxDate={formData.endDate || null}
                      placeholder="Select multiple holiday dates"
                      className={`form-control ${errors.holidayDates ? 'is-invalid' : ''}`}
                      inputClass="form-control"
                      containerStyle={{ width: "91%" }}
                    />
                    <button
                      className="btn btn-outline-secondary"
                      type="button"
                      onClick={() => setFormData(prev => ({ ...prev, holidayDates: [] }))}
                    >
                      Clear All
                    </button>
                  </div>
                  {formData.holidayDates.length > 0 && (
                    <div className="mt-3">
                      <div className="d-flex flex-wrap gap-2">
                        {formData.holidayDates.map((date, index) => (
                          <span
                            key={index}
                            className="badge bg-warning text-dark d-flex align-items-center"
                            style={{ fontSize: "0.7rem" }}
                          >
                            {formatDate(date)}
                            <i
                              className="fas fa-times ms-2"
                              onClick={() => removeHolidayDate(index)}
                              style={{ cursor: "pointer" }}
                            />
                          </span>
                        ))}
                      </div>
                    </div>
                  )}
                  {errors.holidayDates && <div className="text-danger small mt-1">{errors.holidayDates}</div>}
                  <div className="form-text">Select dates that should be marked as holidays</div>
                </div>
              </div>

              {/* Summary Card */}
              {(formData.course || formData.startDate) && (
                <div className="row mb-4">
                  <div className="col-12">
                    <div className="card bg-light border-primary">
                      <div className="card-header bg-primary text-white">
                        <h6 className="mb-0 text-white">📋 Generation Summary</h6>
                      </div>
                      <div className="card-body">
                        <div className="row">
                          <div className="col-md-6">
                            <p className="mb-2">
                              <strong>Batch:</strong> {batchId ? ` (ID: ${batchId})` : ''}
                            </p>
                            <p className="mb-2">
                              <strong>Course:</strong> {getSelectedCourseLabel()}
                            </p>
                            <p className="mb-2">
                              <strong>Type:</strong> {formData.batchType}
                            </p>
                            <p className="mb-2">
                              <strong>Frequency:</strong> {formData.frequency}
                            </p>
                          </div>
                          <div className="col-md-6">
                            <p className="mb-2">
                              <strong>Date Range:</strong>{' '}
                              {formData.startDate
                                ? `${formatDate(formData.startDate)} ${
                                  formData.endDate
                                    ? `to ${formatDate(formData.endDate)}`
                                    : `(90 days)`
                                }`
                                : 'Not specified'}
                            </p>
                            <p className="mb-2">
                              <strong>Time:</strong>{' '}
                              {formData.startTime && formData.endTime
                                ? `${formData.startTime} - ${formData.endTime} (${calculateDuration()})`
                                : 'Not specified'}
                            </p>
                            <p className="mb-2">
                              <strong>Days:</strong> {getSelectedDaysCount()} selected
                            </p>
                            <p className="mb-2">
                              <strong>Holidays:</strong> {formData.holidayDates.length} dates
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Buttons */}
              <div className="row">
                <div className="col-12">
                  <div className="d-flex gap-2 flex-wrap">
                    <button
                      type="button"
                      className="btn btn-primary px-4 py-2"
                      onClick={handlePreview}
                      disabled={isGenerating || isSaving}
                    >
                      {isGenerating ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Generating Preview...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-eye me-1"></i> Generate Preview
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-success px-4 py-2"
                      onClick={handleSaveSchedule}
                      disabled={isGenerating || isSaving || scheduleData.length === 0}
                    >
                      {isSaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm me-2" role="status"></span>
                          Saving...
                        </>
                      ) : (
                        <>
                          <i className="fas fa-save me-1"></i> Save Schedule
                        </>
                      )}
                    </button>

                    <button
                      type="button"
                      className="btn btn-outline-secondary px-4 py-2"
                      onClick={resetForm}
                      disabled={isGenerating || isSaving}
                    >
                      <i className="fas fa-sync-alt me-1"></i> Reset Form
                    </button>

                    <a
                      href="/teacher-batches"
                      className="btn btn-outline-primary px-4 py-2"
                    >
                      <i className="fas fa-arrow-left me-1"></i> Back to Batches
                    </a>
                  </div>

                  <div className="mt-2">
                    <small className="text-muted">
                      💡 <strong>Tip:</strong> First click "Generate Preview" to see the schedule, then "Save Schedule" to create it.
                      {scheduleData.length === 0 && " The Save button will be enabled after generating a preview."}
                    </small>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Schedule Preview Table */}
      {showPreview && (
        <div className="row mt-4" id="schedule-preview">
          <div className="col-12">
            <div className="card shadow-sm border-0">
              <div className="card-header bg-success text-white d-flex justify-content-between align-items-center">
                <h5 className="mb-0 text-white">📅 Schedule Preview</h5>
                <div className="d-flex gap-2">
                  <span className="badge bg-light text-dark fs-6 d-inline-flex align-items-center justify-content-center">
                    Total: {scheduleData.length} sessions
                  </span>
                  {scheduleData.filter(s => s.holiday === 'Yes').length > 0 && (
                    <span className="badge bg-warning text-dark fs-6 d-inline-flex align-items-center justify-content-center">
                      Holidays: {scheduleData.filter(s => s.holiday === 'Yes').length}
                    </span>
                  )}
                  <button
                    className="btn btn-sm btn-outline-light"
                    onClick={() => setShowPreview(false)}
                    title="Hide Preview"
                  >
                    <i className="fas fa-times"></i>
                  </button>
                </div>
              </div>
              <div className="card-body p-0">
                {scheduleData.length > 0 ? (
                  <>
                    <div className="table-responsive" style={{ maxHeight: '400px', overflowY: 'auto' }}>
                      <table className="table table-hover mb-0">
                        <thead className="table-light">
                          <tr>
                            <th className="px-4 py-3 fw-semibold">#</th>
                            <th className="px-4 py-3 fw-semibold">Date</th>
                            <th className="px-4 py-3 fw-semibold">Day</th>
                            <th className="px-4 py-3 fw-semibold">Time</th>
                            <th className="px-4 py-3 fw-semibold">Duration</th>
                            <th className="px-4 py-3 fw-semibold">Status</th>
                          </tr>
                        </thead>
                        <tbody>
                          {scheduleData.map((item) => (
                            <tr key={item.id} className={item.holiday === 'Yes' ? 'table-warning' : ''}>
                              <td className="px-4 py-3 fw-medium text-primary">
                                {String(item.id).padStart(2, '0')}
                              </td>
                              <td className="px-4 py-3">{formatDate(item.date)}</td>
                              <td className="px-4 py-3">
                                {item.day}
                              </td>
                              <td className="px-4 py-3 font-monospace">{item.time}</td>
                              <td className="px-4 py-3">{calculateDuration()}</td>
                              <td className="px-4 py-3">
                                <span className={`badge ${item.holiday === 'Yes'
                                  ? 'bg-warning text-dark'
                                  : 'bg-success'
                                  }`}>
                                  {item.status}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                    <div className="px-4 py-3 bg-light border-top">
                      <div className="row align-items-center">
                        <div className="col-md-8">
                          <div className="d-flex flex-wrap gap-3">
                            <small className="text-muted">
                              <i className="fas fa-calendar-check me-1"></i>
                              Scheduled Sessions: <strong>{scheduleData.filter(s => s.holiday === 'No').length}</strong>
                            </small>
                            {scheduleData.filter(s => s.holiday === 'Yes').length > 0 && (
                              <small className="text-muted">
                                <i className="fas fa-calendar-times me-1"></i>
                                Holiday Sessions: <strong>{scheduleData.filter(s => s.holiday === 'Yes').length}</strong>
                              </small>
                            )}
                            <small className="text-muted">
                              <i className="fas fa-clock me-1"></i>
                              Total Duration: <strong>
                                {Math.round(
                                  scheduleData.filter(s => s.holiday === 'No').length *
                                  parseFloat(calculateDuration().split(' ')[0] || 0) * 10
                                ) / 10} hours
                              </strong>
                            </small>
                          </div>
                        </div>
                        <div className="col-md-4 text-md-end">
                          <small className="text-muted">
                            {scheduleData.length >= 50 && 'Showing first 50 entries. '}
                            {scheduleData.some(item => item.holiday === 'Yes') && 'Holiday dates are highlighted.'}
                          </small>
                        </div>
                      </div>
                    </div>
                  </>
                ) : (
                  <div className="text-center py-5">
                    <div className="mb-3">
                      <i className="fas fa-calendar-plus fa-3x text-muted"></i>
                    </div>
                    <h6 className="text-muted">No Schedule Generated</h6>
                    <p className="text-muted mb-0">
                      Fill in the form parameters and click "Generate Preview" to see the schedule.
                    </p>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Quick Stats Cards */}
      {showPreview && scheduleData.length > 0 && (
        <div className="row mt-4">
          <div className="col-md-3">
            <div className="card bg-primary bg-gradient text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-1 text-white">{scheduleData.length}</h4>
                    <p className="mb-0 text-white">Total Sessions</p>
                  </div>
                  <div>
                    <i className="fas fa-calendar-alt fa-2x text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-success bg-gradient text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-1 text-white">{scheduleData.filter(s => s.holiday === 'No').length}</h4>
                    <p className="mb-0 text-white">Scheduled</p>
                  </div>
                  <div>
                    <i className="fas fa-calendar-check fa-2x text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-warning bg-gradient text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-1 text-white">{scheduleData.filter(s => s.holiday === 'Yes').length}</h4>
                    <p className="mb-0 text-white">Holidays</p>
                  </div>
                  <div>
                    <i className="fas fa-calendar-times fa-2x text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-md-3">
            <div className="card bg-info bg-gradient text-white">
              <div className="card-body">
                <div className="d-flex justify-content-between align-items-center">
                  <div>
                    <h4 className="mb-1 text-white">
                      {Math.round(
                        scheduleData.filter(s => s.holiday === 'No').length *
                        parseFloat(calculateDuration().split(' ')[0] || 0)
                      )}h
                    </h4>
                    <p className="mb-0 text-white">Total Hours</p>
                  </div>
                  <div>
                    <i className="fas fa-clock fa-2x text-white"></i>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default IFGenerateScheduleForm;
